import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productFilterSlice } from '../../store/productFilterSlice';
import { useAppDispatch, useAppSelector } from '../store/useStore';
import {
  ProductFilterOptionValueCategoryEnum,
  ProductFilterOptionValueSorterEnum,
  ProductFilterOptionValueStatusEnum,
} from '../../constants/product/productFilter.constant';

// PLP 진입 시 달고 들어온 Search Param 을 필터 옵션에 반영하기 위한 hooks
export const useChangeFilterOptionIsSelectedBySearchParam = () => {
  const dispatch = useAppDispatch();
  const productFilterStates = useAppSelector((state) => state.productFilter);
  const productFilterActions = productFilterSlice.actions;

  const [searchParams] = useSearchParams();

  const sortSearchParam = useMemo<string>(() => {
    if (searchParams.has('sort')) {
      return searchParams.get('sort')!;
    }
    return '';
  }, []);

  const categorySearchParam = useMemo<string>(() => {
    if (searchParams.has('category')) {
      return searchParams.get('category')!;
    }
    return '';
  }, []);

  const statusSearchParam = useMemo<string>(() => {
    if (searchParams.has('status')) {
      return searchParams.get('status')!;
    }
    return '';
  }, []);

  const brandSearchParam = useMemo<string>(() => {
    if (searchParams.has('brand')) {
      return searchParams.get('brand')!;
    }
    return '';
  }, []);

  const shouldResetBrandFilter = searchParams.has('brand') && !brandSearchParam;

  useEffect(() => {
    // search param 체크가 아직 안되었고, 필터 옵션에 brands가 모두 추가되었을 때 수행
    if (!productFilterStates.isSearchParamChecked && productFilterStates.isFilterOptionsAdded.brands) {
      // searchParams 이 있으면 수행
      if (searchParams) {
        // sortSearchParam 이 있는 경우
        if (sortSearchParam) {
          Object.values(ProductFilterOptionValueSorterEnum).forEach((sortOptionValue) => {
            if (sortSearchParam.includes(sortOptionValue)) {
              const targetFilter = productFilterStates.globalProductFilters.find((filter) => filter.key === 'sorter');
              const targetOption = targetFilter?.options.find((option) => option.value === sortOptionValue);

              if (targetFilter && targetOption) {
                dispatch(
                  productFilterActions.changeProductFilterOptionIsSelected({
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // categorySearchParam 이 있는 경우
        if (categorySearchParam) {
          Object.values(ProductFilterOptionValueCategoryEnum).forEach((categoryOptionValue) => {
            if (categorySearchParam.includes(categoryOptionValue)) {
              const targetFilter = productFilterStates.globalProductFilters.find(
                (filter) => filter.key === 'filter_category',
              );
              const targetOption = targetFilter?.options.find((option) => option.value === categoryOptionValue);

              if (targetFilter && targetOption) {
                dispatch(
                  productFilterActions.changeProductFilterOptionIsSelected({
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // statusSearchParam 이 있는 경우
        if (statusSearchParam) {
          Object.values(ProductFilterOptionValueStatusEnum).forEach((statusOptionValue) => {
            if (statusSearchParam.includes(statusOptionValue as string)) {
              const targetFilter = productFilterStates.globalProductFilters.find(
                (filter) => filter.key === 'filter_status',
              );
              const targetOption = targetFilter?.options.find((option) => option.value === statusOptionValue);

              if (targetFilter && targetOption) {
                dispatch(
                  productFilterActions.changeProductFilterOptionIsSelected({
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        if (shouldResetBrandFilter) {
          const targetFilter = productFilterStates.globalProductFilters.find((filter) => filter.key === 'filter_brand');
          if (targetFilter) {
            dispatch(productFilterActions.resetOneFilter(targetFilter));
          }
        }

        // brandSearchParam 이 있는 경우
        if (brandSearchParam) {
          brandSearchParam.split(',').forEach((brandOptionValue) => {
            if (brandSearchParam.includes(brandOptionValue)) {
              const targetFilter = productFilterStates.globalProductFilters.find(
                (filter) => filter.key === 'filter_brand',
              );
              const targetOption = targetFilter?.options.find((option) => option.value === brandOptionValue);

              if (targetFilter && targetOption) {
                dispatch(
                  productFilterActions.changeProductFilterOptionIsSelected({
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }
      }

      dispatch(productFilterActions.changeIsSearchParamChecked(true));
    }
  }, [productFilterStates.isSearchParamChecked, productFilterStates.isFilterOptionsAdded.brands, searchParams]);
};
